// frontend/src/components/OrderForm.js
import React, { useState, useCallback, useRef } from 'react'; // Corrected import syntax
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// Helper function to convert full province/state name to 2-letter code
const getProvinceCode = (provinceName, countryCode) => {
    const provinceMapCA = {
        "Alberta": "AB", "British Columbia": "BC", "Manitoba": "MB", "New Brunswick": "NB",
        "Newfoundland and Labrador": "NL", "Nova Scotia": "NS", "Ontario": "ON", "Prince Edward Island": "PE", "Quebec": "QC", "Saskatchewan": "SK",
        "Northwest Territories": "NT", "Nunavut": "NU", "Yukon": "YT",
    };
    const usStatesMap = {
        "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
        "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
        "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
        "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
        "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS", "Missouri": "MO",
        "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH", "New Jersey": "NJ",
        "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH",
        "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
        "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT",
        "Virginia": "VA", "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
    };

    if (countryCode === 'CA') {
        return provinceMapCA[provinceName] || provinceName; // Return code or original if not found
    } else if (countryCode === 'US') {
        // Find the code from usStatesMap, or return original if not found
        return usStatesMap[provinceName] || provinceName;
    }
    return provinceName; // For other countries, return as is (they use text input)
};


// CheckoutForm component (nested inside OrderForm for simplicity, but can be separate)
const CheckoutForm = ({ orderData, backendUrl, onPaymentSuccess, onPaymentError, selectedService }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);
        setMessage(null); // Clear previous messages

        // MODIFIED: Confirm the payment on the client side with manual capture
        const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/order-success`,
            },
            redirect: 'if_required', // Do not redirect immediately, handle success/failure in code
            // IMPORTANT: Set capture_method to 'manual' to authorize but not immediately capture funds
            // This allows us to capture or refund later based on label generation success.
            capture_method: 'manual'
        });

        if (stripeError) {
            setMessage(stripeError.message);
            onPaymentError(stripeError.message);
            setIsLoading(false);
            return;
        }

        // Check if the payment intent is in a state where it can be captured
        if (paymentIntent && paymentIntent.status === 'requires_capture') {
            setMessage("Payment authorized. Attempting to generate shipping label...");
            console.log("Payment Intent authorized:", paymentIntent);

            try {
                // Call your backend to generate the shipping label
                const labelResponse = await fetch(`${backendUrl}/generate-shipping-label`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        orderId: orderData.id, // Pass the Firestore order ID
                        orderData: orderData,
                        selectedService: selectedService,
                        paymentIntentId: paymentIntent.id // Pass the payment intent ID
                    }),
                });

                const labelData = await labelResponse.json();

                if (labelResponse.ok) {
                    // If label generation is successful, capture the payment
                    setMessage("Shipping label generated successfully! Capturing payment...");
                    console.log("Label generated, attempting to capture payment intent:", paymentIntent.id);

                    // Call backend to capture the payment intent
                    // Backend needs a new endpoint like POST /capture-payment
                    // This endpoint should call stripe.paymentIntents.capture(paymentIntentId)
                    const captureResponse = await fetch(`${backendUrl}/capture-payment`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ paymentIntentId: paymentIntent.id })
                    });
                    const captureData = await captureResponse.json();

                    if (captureResponse.ok && captureData.status === 'succeeded') {
                        setMessage("Payment captured and shipping label generated successfully!");
                        onPaymentSuccess(labelData); // Pass the labelData including labelUrl and trackingNumber
                    } else {
                        // If capture fails, refund the authorized amount
                        console.error("Failed to capture payment after label generation:", captureData.error || 'Unknown capture error');
                        setMessage(`Payment capture failed: ${captureData.error || 'Unknown error'}. Initiating refund.`);
                        // Call backend to refund the payment intent
                        // Backend needs a new endpoint like POST /refund-payment
                        // This endpoint should call stripe.refunds.create({ payment_intent: paymentIntentId })
                        await fetch(`${backendUrl}/refund-payment`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ paymentIntentId: paymentIntent.id })
                        });
                        onPaymentError(`Order processed, label generated, but payment capture failed. A refund has been initiated.`);
                    }

                } else {
                    // If label generation fails, refund the authorized amount
                    console.error("Error generating shipping label:", labelData.message || labelData.error || 'Failed to generate shipping label.');
                    setMessage(`Error generating label: ${labelData.message || labelData.error || 'Unknown error'}. Initiating refund.`);

                    // Call backend to refund the payment intent
                    // Backend needs a new endpoint like POST /refund-payment
                    // This endpoint should call stripe.refunds.create({ payment_intent: paymentIntentId })
                    await fetch(`${backendUrl}/refund-payment`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ paymentIntentId: paymentIntent.id })
                    });
                    onPaymentError(`Order could not be completed. Label generation failed, and your payment has been refunded.`);
                }
            } catch (labelError) {
                console.error("Critical error during label generation or capture:", labelError);
                setMessage(`A critical error occurred: ${labelError.message}. Initiating refund.`);
                // Ensure refund is attempted even on critical client-side errors during label generation/capture
                await fetch(`${backendUrl}/refund-payment`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ paymentIntentId: paymentIntent.id })
                });
                onPaymentError(`A critical error occurred. Your payment has been refunded.`);
            }
        } else {
            // This case handles scenarios where paymentIntent.status is not 'requires_capture'
            // after confirmPayment, which implies an unexpected state or a direct failure.
            setMessage("Payment did not succeed or was in an unexpected state. Please try again.");
            onPaymentError("Payment not succeeded.");
        }
        setIsLoading(false);
    };

    const paymentElementOptions = {
        layout: "tabs",
    };

    return (
        <form id="payment-form" onSubmit={handleSubmit} className="mt-6 p-6 bg-blue-50 rounded-lg shadow-inner">
            <h3 className="text-xl font-semibold text-blue-800 mb-4">Payment Information</h3>
            <PaymentElement id="payment-element" options={paymentElementOptions} className="mb-4" />
            <button disabled={isLoading || !stripe || !elements} id="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed">
                <span id="button-text">
                    {isLoading ? <div className="spinner" id="spinner"></div> : "Pay now"}
                </span>
            </button>
            {/* Show any error or success messages */}
            {message && <div id="payment-message" className="mt-4 text-center text-sm font-medium text-red-600">{message}</div>}
        </form>
    );
};


const OrderForm = ({ backendUrl, stripeClient }) => { // Added stripeClient to props
    // Initial state for form fields to allow easy reset
    const initialFormState = {
        senderName: '',
        senderAddress: '',
        senderCity: '',
        senderProvince: '',
        senderPostalCode: '',
        senderCountry: 'CA',
        senderPhone: '',
        senderEmail: '',
        senderIsCommercial: false,
        senderAttn: '',
        senderSuite: '',
        senderDepartment: '',
        senderExt: '',

        receiverName: '',
        receiverAddress: '',
        receiverCity: '',
        receiverProvince: '',
        receiverPostalCode: '',
        receiverCountry: 'CA',
        receiverPhone: '',
        receiverEmail: '',
        receiverIsCommercial: false,
        receiverAttn: '',
        receiverSuite: '',
        receiverDepartment: '',
        receiverExt: '',

        packageWeight: '',
        packageLength: '',
        packageWidth: '',
        packageHeight: '',
        packageUnits: 'imperial', // 'imperial' or 'metric'
        packageType: 'package', // 'package', 'envelope', 'pallet'
        packageContent: 'goods', // 'goods' or 'documents'
        packageDescription: '', // Made required

        signatureRequired: false,
        saturdayDelivery: false,
        shippingDate: new Date().toISOString().split('T')[0], // Set to today's date
        reference: '',
        driverInstructions: '',
        addressCorrection: false,
        shipmentTrackingEmails: '',
        insuranceValue: '',
        insuranceDescription: '',
        codMethod: '',
        codPayableTo: '',
        codReceiverPhone: '',
        codAmount: '',
        codCurrency: 'CAD',
        paymentPayer: 'F',
        paymentCurrency: 'CAD', // New field for payment currency

        soldToName: '',
        soldToAttn: '', // Made required
        soldToAddress: '',
        soldToSuite: '',
        soldToCity: '',
        soldToCountry: '',
        soldToState: '',
        soldToPostalCode: '',
        soldToPhone: '',
        soldToExt: '',
        soldToDepartment: '',
        soldToIsCommercial: false,
        dutiesPayer: 'C', // Changed default to 'C' for Collect (Receiver)
        dutiesPayerAccount: '',
        reasonForExport: 'P', // Default to 'P' (Permanent)
        isNafta: false,

        inquiryCompany: '',
        inquiryName: '',
        brokerId: '',
        senderTaxId: '',
        receiverTaxId: '',
        inquiryPhone: '',
        inquiryPhoneExt: '',

        declaredItems: [{
            product_name: '', description: '', HS_code: '', country_of_origin: '', // Country of origin will be a 2-letter code
            quantity: '', unit_price: '', unit_weight: '', unit_of_measurement: 'pounds'
        }],
    };

    const [formState, setFormState] = useState(initialFormState);

    // Destructure formState for easier access in JSX
    const {
        senderName, senderAddress, senderCity, senderProvince, senderPostalCode, senderCountry, senderPhone, senderEmail, senderIsCommercial, senderAttn, senderSuite, senderDepartment, senderExt,
        receiverName, receiverAddress, receiverCity, receiverProvince, receiverPostalCode, receiverCountry, receiverPhone, receiverEmail, receiverIsCommercial, receiverAttn, receiverSuite, receiverDepartment, receiverExt,
        packageWeight, packageLength, packageWidth, packageHeight, packageUnits, packageType, packageContent, packageDescription,
        signatureRequired, saturdayDelivery, shippingDate, reference, driverInstructions, shipmentTrackingEmails, paymentCurrency,
        soldToName, soldToAttn, soldToAddress, soldToSuite, soldToCity, soldToCountry, soldToState, soldToPostalCode, soldToPhone, soldToExt, soldToDepartment, soldToIsCommercial,
        dutiesPayer, dutiesPayerAccount, reasonForExport, isNafta, inquiryCompany, inquiryName, brokerId, senderTaxId, receiverTaxId, inquiryPhone, inquiryPhoneExt, declaredItems
    } = formState;


    const [shippingOptions, setShippingOptions] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [distance, setDistance] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [currentOrderData, setCurrentOrderData] = useState(null);
    const [nearestDropoff, setNearestDropoff] = useState(null);
    const [labelDetails, setLabelDetails] = useState(null); // To store labelUrl and trackingNumber

    // New states for label content loading and error
    const [labelContentLoading, setLabelContentLoading] = useState(false); // Controls the overlay on the label
    const [labelContentError, setLabelContentError] = useState(false); // Controls the error message on the label
    const labelLoadTimeoutRef = useRef(null); // Ref to store the timeout ID for actual label content loading

    // New state to control when the label iframe/img is actually rendered
    const [isLabelDisplayReady, setIsLabelDisplayReady] = useState(false);
    const initialLabelDisplayTimeoutRef = useRef(null); // Ref for the initial 10-second delay

    const isInternationalShipment = senderCountry !== receiverCountry;


    const handleAddDeclaredItem = () => {
        setFormState(prevState => ({
            ...prevState,
            declaredItems: [...prevState.declaredItems, {
                product_name: '', description: '', HS_code: '', country_of_origin: '', // Country of origin will be a 2-letter code
                quantity: '', unit_price: '', unit_weight: '', unit_of_measurement: packageUnits === 'imperial' ? 'pounds' : 'kilograms'
            }]
        }));
    };

    const handleRemoveDeclaredItem = (index) => {
        setFormState(prevState => ({
            ...prevState,
            declaredItems: prevState.declaredItems.filter((_, i) => i !== index)
        }));
    };

    const handleDeclaredItemChange = (index, field, value) => {
        setFormState(prevState => {
            const newItems = [...prevState.declaredItems];
            newItems[index][field] = value;
            return { ...prevState, declaredItems: newItems };
        });
    };

    const handleCalculateShipping = async (e) => {
        console.log("handleCalculateShipping called."); // Log at the start of the function
        e.preventDefault();
        setLoading(true);
        setError(null);
        setShippingOptions([]);
        setSelectedService(null);
        setClientSecret(null);
        setNearestDropoff(null);
        setLabelDetails(null); // Reset label details on new calculation
        setLabelContentLoading(false); // Reset label content loading state
        setLabelContentError(false); // Reset label content error state
        setIsLabelDisplayReady(false); // Reset label display readiness
        clearTimeout(labelLoadTimeoutRef.current); // Clear any existing timeout
        clearTimeout(initialLabelDisplayTimeoutRef.current); // Clear initial display timeout

        // Ensure shippingDate is current or future - FIX: Use setUTCHours for consistent comparison
        const today = new Date();
        today.setUTCHours(0, 0, 0, 0); // Normalize to start of UTC day
        const selectedDate = new Date(shippingDate);
        selectedDate.setUTCHours(0, 0, 0, 0); // Normalize to start of UTC day

        // Check if the selected date is in the past (before today)
        // Using getTime() for reliable comparison of Date objects
        if (selectedDate.getTime() < today.getTime()) {
            setError('Shipping date cannot be in the past. Please select today or a future date.');
            setLoading(false); // Stop loading
            setShippingOptions([]); // Ensure no options are shown
            return; // Stop the function execution
        }

        try {
            const payload = {
                sender: {
                    name: senderName,
                    address: senderAddress,
                    city: senderCity,
                    // MODIFIED: Convert to 2-letter code if CA or US
                    province: getProvinceCode(senderProvince, senderCountry),
                    postal_code: senderPostalCode,
                    country: senderCountry,
                    phone: senderPhone,
                    email: senderEmail,
                    is_commercial: senderIsCommercial,
                    attn: senderAttn,
                    suite: senderSuite,
                    department: senderDepartment,
                    ext: senderExt
                },
                receiver: {
                    name: receiverName,
                    address: receiverAddress,
                    city: receiverCity,
                    // MODIFIED: Convert to 2-letter code if CA or US
                    province: getProvinceCode(receiverProvince, receiverCountry),
                    postal_code: receiverPostalCode,
                    country: receiverCountry,
                    phone: receiverPhone,
                    email: receiverEmail,
                    is_commercial: receiverIsCommercial,
                    attn: receiverAttn,
                    suite: receiverSuite,
                    department: receiverDepartment,
                    ext: receiverExt
                },
                package: {
                    weight: parseFloat(packageWeight),
                    dimensions: {
                        length: parseFloat(packageLength),
                        width: parseFloat(packageWidth),
                        height: parseFloat(packageHeight),
                    },
                    units: packageUnits,
                    type: packageType,
                    content: packageContent,
                    description: packageDescription // This field is now required by the frontend
                },
                deliveryType: "standard", // This can be dynamic if you add more options
                options: {
                    signature_required: signatureRequired,
                    saturday_delivery: saturdayDelivery,
                    shipping_date: shippingDate, // Use the selected date (which is now guaranteed to be today or future)
                    reference: reference,
                    driver_instructions: driverInstructions,
                    // Removed address_correction
                    shipment_tracking_emails: shipmentTrackingEmails,
                    // Removed insurance
                    // Removed cod
                    // Removed payment payer
                },
                payment: { // Added payment object for currency
                    payer: formState.paymentPayer, // Use existing paymentPayer
                    currency: paymentCurrency // Pass the selected currency
                }
            };

            // Conditionally add international details to the payload
            if (isInternationalShipment) {
                payload.sold_to = {
                    sold_to_address: {
                        name: soldToName,
                        attn: soldToAttn, // This field is now required by the frontend
                        address: soldToAddress,
                        suite: soldToSuite,
                        city: soldToCity,
                        country: soldToCountry,
                        // MODIFIED: Convert to 2-letter code if CA or US
                        state: getProvinceCode(soldToState, soldToCountry),
                        postal_code: soldToPostalCode,
                        phone: soldToPhone,
                        ext: soldToExt,
                        department: soldToDepartment,
                        is_commercial: soldToIsCommercial
                    },
                    duties_payer: dutiesPayer,
                    duties_payer_account: dutiesPayerAccount,
                    reason_for_export: reasonForExport, // Now sends the single letter
                    is_nafta: isNafta
                };
                payload.inquiry = {
                    company: inquiryCompany,
                    name: inquiryName,
                    broker_id: brokerId,
                    sender_tax_id: senderTaxId,
                    receiver_tax_id: receiverTaxId,
                    inquiry_phone: inquiryPhone,
                    inquiry_phone_ext: inquiryPhoneExt
                };
                // For /ship/rates, declared_items is just an array of items.
                // The backend will restructure it for /ship/prepare.
                payload.declared_items = declaredItems.map(item => ({
                    ...item,
                    quantity: parseInt(item.quantity),
                    unit_price: parseFloat(item.unit_price),
                    unit_weight: parseFloat(item.unit_weight),
                }));
            }


            console.log("Sending calculate-shipping request with payload:", payload); // Log the payload
            const response = await fetch(`${backendUrl}/calculate-shipping`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            console.log("Received raw calculate-shipping response:", response); // Log the raw response object
            const data = await response.json();
            console.log("Received parsed calculate-shipping response data:", data); // Log the parsed data

            if (response.ok) {
                setShippingOptions(data.shippingOptions);
                setDistance(data.distanceKm);
                setError(null); // Clear any previous error
            } else {
                let displayErrorMessage = data.error || `Failed to fetch shipping rates. Status: ${response.status}. Please check your input.`;

                // Attempt to make the error message more human-friendly
                if (displayErrorMessage.includes("Flagship API returned non-OK status:")) {
                    const parts = displayErrorMessage.split(" - ");
                    if (parts.length > 1) {
                        const detailedPart = parts[1]; // This contains "Unknown error" or "Specific issues:..." or "Message:..."
                        if (detailedPart.startsWith("Specific issues:")) {
                            // Example: "Specific issues: sender.province: The selected province is invalid.; receiver.city: City is required."
                            const issues = detailedPart.substring("Specific issues:".length).trim();
                            displayErrorMessage = `Please correct the following: ${issues}`;
                        } else if (detailedPart.startsWith("Message:")) {
                            // Example: "Message: Invalid input."
                            displayErrorMessage = detailedPart.substring("Message:".length).trim();
                        } else if (detailedPart.startsWith("Error:")) {
                            // Example: "Error: Some generic error."
                            displayErrorMessage = detailedPart.substring("Error:".length).trim();
                        } else {
                            // Fallback for "Unknown error" or other direct messages
                            displayErrorMessage = detailedPart;
                        }
                    }
                }

                setError(displayErrorMessage);
                setShippingOptions([]); // Ensure no options are shown on error
                console.error("Error fetching shipping rates:", data.error || 'Unknown error');
            }
        } catch (err) {
            console.error("Error during shipping calculation (catch block):", err); // Log errors from the catch block
            setError('Failed to connect to the server or an unexpected error occurred.');
            setShippingOptions([]); // Ensure no options are shown on critical error
        } finally {
            setLoading(false);
            console.log("handleCalculateShipping finished. Loading set to false.");
        }
    };

    const handleServiceSelect = useCallback(async (service) => {
        console.log("handleServiceSelect called with service:", service);
        setSelectedService(service);
        setError(null); // Clear any previous errors
        setClientSecret(null); // Reset client secret when a new service is selected
        setNearestDropoff(null); // Reset dropoff info
        setLabelDetails(null); // Reset label details
        setLabelContentLoading(false); // Reset label content loading state
        setLabelContentError(false); // Reset label content error state
        setIsLabelDisplayReady(false); // Reset label display readiness
        clearTimeout(labelLoadTimeoutRef.current); // Clear any existing timeout
        clearTimeout(initialLabelDisplayTimeoutRef.current); // Clear initial display timeout

        // Save order to Firestore first to get an ID
        try {
            setLoading(true); // Set loading for the entire service selection process
            const orderToSave = {
                sender: {
                    name: senderName, address: senderAddress, city: senderCity,
                    // MODIFIED: Convert to 2-letter code if CA or US
                    province: getProvinceCode(senderProvince, senderCountry),
                    postal_code: senderPostalCode, country: senderCountry, phone: senderPhone, email: senderEmail,
                    is_commercial: senderIsCommercial, attn: senderAttn, suite: senderSuite, department: senderDepartment, ext: senderExt
                },
                receiver: {
                    name: receiverName, address: receiverAddress, city: receiverCity,
                    // MODIFIED: Convert to 2-letter code if CA or US
                    province: getProvinceCode(receiverProvince, receiverCountry),
                    postal_code: receiverPostalCode, country: receiverCountry, phone: receiverPhone, email: receiverEmail,
                    is_commercial: receiverIsCommercial, attn: receiverAttn, suite: receiverSuite, department: receiverDepartment, ext: receiverExt
                },
                package: {
                    weight: parseFloat(packageWeight),
                    dimensions: { length: parseFloat(packageLength), width: parseFloat(packageWidth), height: parseFloat(packageHeight) },
                    units: packageUnits, type: packageType, content: packageContent, description: packageDescription
                },
                deliveryType: "standard",
                options: {
                    signature_required: signatureRequired, saturday_delivery: saturdayDelivery, shipping_date: shippingDate,
                    reference: reference, driver_instructions: driverInstructions,
                    shipment_tracking_emails: shipmentTrackingEmails,
                },
                payment: { // Include payment details in order to save to Firestore
                    payer: formState.paymentPayer,
                    currency: paymentCurrency
                },
                selectedService: service, // Save the selected service details
                totalCost: service.totalCost, // Store total cost from selected service
                distanceKm: distance
            };

            // Conditionally add international details to the orderToSave
            if (isInternationalShipment) {
                orderToSave.sold_to = {
                    sold_to_address: {
                        name: soldToName, attn: soldToAttn, address: soldToAddress, suite: soldToSuite, city: soldToCity,
                        country: soldToCountry,
                        // MODIFIED: Convert to 2-letter code if CA or US
                        state: getProvinceCode(soldToState, soldToCountry),
                        postal_code: soldToPostalCode,
                        phone: soldToPhone, ext: soldToExt, department: soldToDepartment, is_commercial: soldToIsCommercial
                    },
                    duties_payer: dutiesPayer, duties_payer_account: dutiesPayerAccount, reason_for_export: reasonForExport, // Now saves the single letter
                    is_nafta: isNafta
                };
                orderToSave.inquiry = {
                    company: inquiryCompany, name: inquiryName, broker_id: brokerId, sender_tax_id: senderTaxId,
                    receiver_tax_id: receiverTaxId, inquiry_phone: inquiryPhone, inquiry_phone_ext: inquiryPhoneExt
                };
                orderToSave.declared_items = declaredItems.map(item => ({
                    ...item,
                    quantity: parseInt(item.quantity), unit_price: parseFloat(item.unit_price), unit_weight: parseFloat(item.unit_weight),
                }));
            }

            console.log("Saving order to Firestore with payload:", orderToSave);
            const saveOrderResponse = await fetch(`${backendUrl}/orders`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(orderToSave),
            });

            const saveOrderData = await saveOrderResponse.json();
            if (!saveOrderResponse.ok) {
                throw new Error(saveOrderData.message || 'Failed to save order to Firestore.');
            }
            console.log("Order saved to Firestore:", saveOrderData);
            setCurrentOrderData({ ...orderToSave, id: saveOrderData.id }); // Store order data with Firestore ID

            // Now, create Payment Intent
            // The amount is sent in cents, and we specify 'manual' capture
            console.log("Creating Payment Intent for amount:", Math.round(service.totalCost * 100));
            const paymentIntentResponse = await fetch(`${backendUrl}/create-payment-intent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    amount: Math.round(service.totalCost * 100), // Amount in cents
                    capture_method: 'manual' // Request manual capture
                }),
            });

            const paymentIntentData = await paymentIntentResponse.json();
            if (!paymentIntentResponse.ok) {
                throw new Error(paymentIntentData.error || 'Failed to create payment intent.');
            }
            setClientSecret(paymentIntentData.clientSecret);
            console.log("Payment Intent client secret received with manual capture.");

        } catch (err) {
            console.error("Error during service selection, order saving, or payment intent creation:", err);
            setError(`Order could not be fully processed: ${err.message}. Please try again or contact support.`);
            setSelectedService(null); // Deselect service if there's an error
            setClientSecret(null); // Clear client secret on error
            setCurrentOrderData(null); // Clear current order data on error
        } finally {
            setLoading(false); // Always stop loading, regardless of success or failure
            console.log("handleServiceSelect finished.");
        }
    }, [backendUrl, senderName, senderAddress, senderCity, senderProvince, senderCountry, senderPostalCode, senderPhone, senderEmail, senderIsCommercial, senderAttn, senderSuite, senderDepartment, senderExt,
        receiverName, receiverAddress, receiverCity, receiverProvince, receiverCountry, receiverPostalCode, receiverPhone, receiverEmail, receiverIsCommercial, receiverAttn, receiverSuite, receiverDepartment, receiverExt,
        packageWeight, packageLength, packageWidth, packageHeight, packageUnits, packageType, packageContent, packageDescription, paymentCurrency,
        signatureRequired, saturdayDelivery, shippingDate, reference, driverInstructions, shipmentTrackingEmails, formState.paymentPayer, // Added formState.paymentPayer
        soldToName, soldToAttn, soldToAddress, soldToSuite, soldToCity, soldToCountry, soldToState, soldToPostalCode, soldToPhone, soldToExt, soldToDepartment, soldToIsCommercial,
        dutiesPayer, dutiesPayerAccount, reasonForExport, isNafta, inquiryCompany, inquiryName, brokerId, senderTaxId, receiverTaxId, inquiryPhone, inquiryPhoneExt, declaredItems, distance, isInternationalShipment]);

    // Callback for when the actual label content (iframe/img) loads
    const handleLabelLoad = useCallback(() => {
        clearTimeout(labelLoadTimeoutRef.current); // Clear timeout if label loads successfully
        setLabelContentLoading(false); // Hide the loading overlay
        setLabelContentError(false);
        console.log('Label content loaded successfully. Setting labelContentLoading to false.');
    }, []);

    // Callback for when the actual label content (iframe/img) errors
    const handleLabelError = useCallback((isImageError = false) => {
        clearTimeout(labelLoadTimeoutRef.current); // Clear timeout if label errors
        setLabelContentLoading(false); // Hide the loading overlay
        setLabelContentError(true); // Show the error message
        console.log('Label content error detected. Setting labelContentLoading to false and labelContentError to true.');
        if (isImageError) {
            console.error("Error loading shipping label image.");
        } else {
            console.error("Error loading shipping label iframe content.");
        }
    }, []);

    const handlePaymentSuccess = useCallback(async (labelResponseData) => {
        console.log("Payment and Label Generation Successful!", labelResponseData);
        console.log("Label URL received:", labelResponseData.labelUrl);
        console.log("Tracking Number received:", labelResponseData.trackingNumber);
        console.log("Courier Name received:", labelResponseData.courierName);

        setLabelDetails(labelResponseData); // Store labelUrl and trackingNumber
        setIsLabelDisplayReady(false); // Ensure label is not displayed immediately
        setLabelContentError(false); // Reset content error state
        setError(null); // Clear any general errors from previous steps

        console.log("Starting initial 10-second delay before displaying label.");
        // Start the initial 10-second delay before rendering the label content
        initialLabelDisplayTimeoutRef.current = setTimeout(() => {
            console.log("Initial 10-second delay complete. Preparing to display label.");
            setIsLabelDisplayReady(true); // Now allow the label iframe/img to render
            setLabelContentLoading(true); // Show loading overlay for the actual label content

            // Set a timeout for the actual label content loading (10 seconds)
            labelLoadTimeoutRef.current = setTimeout(() => {
                console.log("Label content load timeout triggered after rendering.");
                handleLabelError(false); // Trigger error if content doesn't load
            }, 10000);
        }, 10000); // Initial 10-second delay

        // Now, fetch nearest drop-off location
        try {
            setLoading(true); // Set loading for dropoff fetch
            const dropoffResponse = await fetch(`${backendUrl}/nearest-dropoff`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    senderAddress: `${senderAddress}, ${senderCity}, ${getProvinceCode(senderProvince, senderCountry)}, ${senderPostalCode}, ${senderCountry}`,
                    courierName: selectedService?.courierName // Use courierName from selectedService, with optional chaining
                }),
            });
            const dropoffData = await dropoffResponse.json();
            if (dropoffResponse.ok && dropoffData.nearestDropoff) {
                setNearestDropoff(dropoffData.nearestDropoff);
            } else {
                console.warn("Could not find nearest drop-off location:", dropoffData.message);
                // Display this as a warning, not a critical error that blocks the label
                setError(prevError => prevError ? `${prevError}\nCould not find nearest drop-off location: ${dropoffData.message}` : `Could not find nearest drop-off location: ${dropoffData.message}`);
            }
        } catch (err) {
            console.error("Error fetching nearest drop-off location:", err);
            // Display this as a warning, not a critical error that blocks the label
            setError(prevError => prevError ? `${prevError}\nError fetching nearest drop-off location: ${err.message}` : `Error fetching nearest drop-off location: ${err.message}`);
        } finally {
            setLoading(false); // Stop loading for dropoff fetch
        }
    }, [backendUrl, senderAddress, senderCity, senderProvince, senderCountry, senderPostalCode, selectedService, handleLabelError]);

    const handlePaymentError = useCallback((msg) => {
        console.error("Payment Error:", msg);
        setError(msg);
        setClientSecret(null); // Clear client secret on payment error
        clearTimeout(initialLabelDisplayTimeoutRef.current); // Clear initial display timeout on payment error
        setLoading(false); // Stop loading if payment fails
    }, []);

    // Function to reset all form states to their initial values
    const handleNewOrder = () => {
        setFormState(initialFormState); // Reset all form fields
        setShippingOptions([]);
        setSelectedService(null);
        setLoading(false);
        setError(null);
        setDistance(null);
        setClientSecret(null);
        setCurrentOrderData(null);
        setNearestDropoff(null);
        setLabelDetails(null);
        setLabelContentLoading(false); // Reset label content loading state
        setLabelContentError(false); // Reset label content error state
        setIsLabelDisplayReady(false); // Reset label display readiness
        clearTimeout(labelLoadTimeoutRef.current); // Clear any existing timeout
        clearTimeout(initialLabelDisplayTimeoutRef.current); // Clear any existing timeout
    };

    const provinces = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Northwest Territories", "Nunavut", "Yukon"];
    const usStates = [
        { name: "Alabama", code: "AL" }, { name: "Alaska", code: "AK" }, { name: "Arizona", code: "AZ" },
        { name: "Arkansas", code: "AR" }, { name: "California", code: "CA" }, { name: "Colorado", code: "CO" },
        { name: "Connecticut", code: "CT" }, { name: "Delaware", code: "DE" }, { name: "Florida", code: "FL" },
        { name: "Georgia", code: "GA" }, { name: "Hawaii", code: "HI" }, { name: "Idaho", code: "ID" },
        { name: "Illinois", code: "IL" }, { name: "Indiana", code: "IN" }, { name: "Iowa", code: "IA" },
        { name: "Kansas", code: "KS" }, { name: "Kentucky", code: "KY" }, { name: "Louisiana", code: "LA" },
        { name: "Maine", code: "ME" }, { name: "Maryland", code: "MD" }, { name: "Massachusetts", code: "MA" },
        { name: "Michigan", code: "MI" }, { name: "Minnesota", code: "MN" }, { name: "Mississippi", code: "MS" },
        { name: "Missouri", code: "MO" }, { name: "Montana", code: "MT" }, { name: "Nebraska", code: "NE" },
        { name: "Nevada", code: "NV" }, { name: "New Hampshire", code: "NH" }, { name: "New Jersey", code: "NJ" },
        { name: "New Mexico", code: "NM" }, { name: "New York", code: "NY" }, { name: "North Carolina", code: "NC" },
        { name: "North Dakota", code: "ND" }, { name: "Ohio", code: "OH" }, { name: "Oklahoma", code: "OK" },
        { name: "Oregon", code: "OR" }, { name: "Pennsylvania", code: "PA" }, { name: "Rhode Island", code: "RI" },
        { name: "South Carolina", code: "SC" }, { name: "South Dakota", code: "SD" }, { name: "Tennessee", code: "TN" },
        { name: "Texas", code: "TX" }, { name: "Utah", code: "UT" }, { name: "Vermont", code: "VT" },
        { name: "Virginia", code: "VA" }, { name: "Washington", code: "WA" }, { name: "West Virginia", code: "WV" }, { name: "Wisconsin", code: "WI" }, { name: "Wyoming", code: "WY" }
    ];
    
    // Mapping for country codes to full names and their default currencies
    const countryData = {
        "CA": { name: "Canada", currency: "CAD" },
        "US": { name: "United States", currency: "USD" },
        "MX": { name: "Mexico", currency: "MXN" },
        "AF": { name: "Afghanistan", currency: "AFN" },
        "AX": { name: "Åland Islands", currency: "EUR" },
        "AL": { name: "Albania", currency: "ALL" },
        "DZ": { name: "Algeria", currency: "DZD" },
        "AS": { name: "American Samoa", currency: "USD" },
        "AD": { name: "Andorra", currency: "EUR" },
        "AO": { name: "Angola", currency: "AOA" },
        "AI": { name: "Anguilla", currency: "XCD" },
        "AQ": { name: "Antarctica", currency: "XCD" },
        "AG": { name: "Antigua & Barbuda", currency: "XCD" },
        "AR": { name: "Argentina", currency: "ARS" },
        "AM": { name: "Armenia", currency: "AMD" },
        "AW": { name: "Aruba", currency: "AWG" },
        "AU": { name: "Australia", currency: "AUD" },
        "AT": { name: "Austria", currency: "EUR" },
        "AZ": { name: "Azerbaijan", currency: "AZN" },
        "BS": { name: "Bahamas", currency: "BSD" },
        "BH": { name: "Bahrain", currency: "BHD" },
        "BD": { name: "Bangladesh", currency: "BDT" },
        "BB": { name: "Barbados", currency: "BBD" },
        "BY": { name: "Belarus", currency: "BYN" },
        "BE": { name: "Belgium", currency: "EUR" },
        "BZ": { name: "Belize", currency: "BZD" },
        "BJ": { name: "Benin", currency: "XOF" },
        "BM": { name: "Bermuda", currency: "BMD" },
        "BT": { name: "Bhutan", currency: "BTN" },
        "BO": { name: "Bolivia", currency: "BOB" },
        "BA": { name: "Bosnia & Herzegovina", currency: "BAM" },
        "BW": { name: "Botswana", currency: "BWP" },
        "BR": { name: "Brazil", currency: "BRL" },
        "IO": { name: "British Indian Ocean Territory", currency: "USD" },
        "VG": { name: "British Virgin Islands", currency: "USD" },
        "BN": { name: "Brunei", currency: "BND" },
        "BG": { name: "Bulgaria", currency: "BGN" },
        "BF": { name: "Burkina Faso", currency: "XOF" },
        "BI": { name: "Burundi", currency: "BIF" },
        "KH": { name: "Cambodia", currency: "KHR" },
        "CM": { name: "Cameroon", currency: "XAF" },
        "CV": { name: "Cape Verde", currency: "CVE" },
        "BQ": { name: "Caribbean Netherlands", currency: "USD" },
        "KY": { name: "Cayman Islands", currency: "KYD" },
        "CF": { name: "Central African Republic", currency: "XAF" },
        "TD": { name: "Chad", currency: "XAF" },
        "CL": { name: "Chile", currency: "CLP" },
        "CN": { name: "China", currency: "CNY" },
        "CX": { name: "Christmas Island", currency: "AUD" },
        "CC": { name: "Cocos (Keeling) Islands", currency: "AUD" },
        "CO": { name: "Colombia", currency: "COP" },
        "KM": { name: "Comoros", currency: "KMF" },
        "CG": { name: "Congo - Brazzaville", currency: "XAF" },
        "CD": { name: "Congo - Kinshasa", currency: "CDF" },
        "CK": { name: "Cook Islands", currency: "NZD" },
        "CR": { name: "Costa Rica", currency: "CRC" },
        "CI": { name: "Côte d’Ivoire", currency: "XOF" },
        "HR": { name: "Croatia", currency: "HRK" }, // Croatia now uses EUR, update if needed
        "CU": { name: "Cuba", currency: "CUP" },
        "CW": { name: "Curaçao", currency: "ANG" },
        "CY": { name: "Cyprus", currency: "EUR" },
        "CZ": { name: "Czechia", currency: "CZK" },
        "DK": { name: "Denmark", currency: "DKK" },
        "DJ": { name: "Djibouti", currency: "DJF" },
        "DM": { name: "Dominica", currency: "XCD" },
        "DO": { name: "Dominican Republic", currency: "DOP" },
        "EC": { name: "Ecuador", currency: "USD" },
        "EG": { name: "Egypt", currency: "EGP" },
        "SV": { name: "El Salvador", currency: "USD" },
        "GQ": { name: "Equatorial Guinea", currency: "XAF" },
        "ER": { name: "Eritrea", currency: "ERN" },
        "EE": { name: "Estonia", currency: "EUR" },
        "SZ": { name: "Eswatini", currency: "SZL" },
        "ET": { name: "Ethiopia", currency: "ETB" },
        "FK": { name: "Falkland Islands", currency: "FKP" },
        "FO": { name: "Faroe Islands", currency: "DKK" },
        "FJ": { name: "Fiji", currency: "FJD" },
        "FI": { name: "Finland", currency: "EUR" },
        "FR": { name: "France", currency: "EUR" },
        "GF": { name: "French Guiana", currency: "EUR" },
        "PF": { name: "French Polynesia", currency: "XPF" },
        "TF": { name: "French Southern Territories", currency: "EUR" },
        "GA": { name: "Gabon", currency: "XAF" },
        "GM": { name: "Gambia", currency: "GMD" },
        "GE": { name: "Georgia", currency: "GEL" },
        "DE": { name: "Germany", currency: "EUR" },
        "GH": { name: "Ghana", currency: "GHS" },
        "GI": { name: "Gibraltar", currency: "GIP" },
        "GR": { name: "Greece", currency: "EUR" },
        "GL": { name: "Greenland", currency: "DKK" },
        "GD": { name: "Grenada", currency: "XCD" },
        "GP": { name: "Guadeloupe", currency: "EUR" },
        "GU": { name: "Guam", currency: "USD" },
        "GT": { name: "Guatemala", currency: "GTQ" },
        "GG": { name: "Guernsey", currency: "GBP" },
        "GN": { name: "Guinea", currency: "GNF" },
        "GW": { name: "Guinea-Bissau", currency: "XOF" },
        "GY": { name: "Guyana", currency: "GYD" },
        "HT": { name: "Haiti", currency: "HTG" },
        "HM": { name: "Heard & McDonald Islands", currency: "AUD" },
        "HN": { name: "Honduras", currency: "HNL" },
        "HK": { name: "Hong Kong SAR China", currency: "HKD" },
        "HU": { name: "Hungary", currency: "HUF" },
        "IS": { name: "Iceland", currency: "ISK" },
        "IN": { name: "India", currency: "INR" },
        "ID": { name: "Indonesia", currency: "IDR" },
        "IR": { name: "Iran", currency: "IRR" },
        "IQ": { name: "Iraq", currency: "IQD" },
        "IE": { name: "Ireland", currency: "EUR" },
        "IM": { name: "Isle of Man", currency: "GBP" },
        "IL": { name: "Israel", currency: "ILS" },
        "IT": { name: "Italy", currency: "EUR" },
        "JM": { name: "Jamaica", currency: "JMD" },
        "JP": { name: "Japan", currency: "JPY" },
        "JE": { name: "Jersey", currency: "GBP" },
        "JO": { name: "Jordan", currency: "JOD" },
        "KZ": { name: "Kazakhstan", currency: "KZT" },
        "KE": { name: "Kenya", currency: "KES" },
        "KI": { name: "Kiribati", currency: "AUD" },
        "KW": { name: "Kuwait", currency: "KWD" },
        "KG": { name: "Kyrgyzstan", currency: "KGS" },
        "LA": { name: "Laos", currency: "LAK" },
        "LV": { name: "Latvia", currency: "EUR" },
        "LB": { name: "Lebanon", currency: "LBP" },
        "LS": { name: "Lesotho", currency: "LSL" },
        "LR": { name: "Liberia", currency: "LRD" },
        "LY": { name: "Libya", currency: "LYD" },
        "LI": { name: "Liechtenstein", currency: "CHF" },
        "LT": { name: "Lithuania", currency: "EUR" },
        "LU": { name: "Luxembourg", currency: "EUR" },
        "MO": { name: "Macao SAR China", currency: "MOP" },
        "MG": { name: "Madagascar", currency: "MGA" },
        "MW": { name: "Malawi", currency: "MWK" },
        "MY": { name: "Malaysia", currency: "MYR" },
        "MV": { name: "Maldives", currency: "MVR" },
        "ML": { name: "Mali", currency: "XOF" },
        "MT": { name: "Malta", currency: "EUR" },
        "MH": { name: "Marshall Islands", currency: "USD" },
        "MQ": { name: "Martinique", currency: "EUR" },
        "MR": { name: "Mauritania", currency: "MRU" },
        "MU": { name: "Mauritius", currency: "MUR" },
        "YT": { name: "Mayotte", currency: "EUR" },
        "FM": { name: "Micronesia", currency: "USD" },
        "MD": { name: "Moldova", currency: "MDL" },
        "MC": { name: "Monaco", currency: "EUR" },
        "MN": { name: "Mongolia", currency: "MNT" },
        "ME": { name: "Montenegro", currency: "EUR" },
        "MS": { name: "Montserrat", currency: "XCD" },
        "MA": { name: "Morocco", currency: "MAD" },
        "MZ": { name: "Mozambique", currency: "MZN" },
        "MM": { name: "Myanmar (Burma)", currency: "MMK" },
        "NA": { name: "Namibia", currency: "NAD" },
        "NR": { name: "Nauru", currency: "AUD" },
        "NP": { name: "Nepal", currency: "NPR" },
        "NL": { name: "Netherlands", currency: "EUR" },
        "NC": { name: "New Caledonia", currency: "XPF" },
        "NZ": { name: "New Zealand", currency: "NZD" },
        "NI": { name: "Nicaragua", currency: "NIO" },
        "NE": { name: "Niger", currency: "XOF" },
        "NG": { name: "Nigeria", currency: "NGN" },
        "NU": { name: "Niue", currency: "NZD" },
        "NF": { name: "Norfolk Island", currency: "AUD" },
        "KP": { name: "North Korea", currency: "KPW" },
        "MK": { name: "North Macedonia", currency: "MKD" },
        "MP": { name: "Northern Mariana Islands", currency: "USD" },
        "NO": { name: "Norway", currency: "NOK" },
        "OM": { name: "Oman", currency: "OMR" },
        "PK": { name: "Pakistan", currency: "PKR" },
        "PW": { name: "Palau", currency: "USD" },
        "PS": { name: "Palestinian Territories", currency: "ILS" },
        "PA": { name: "Panama", currency: "PAB" },
        "PG": { name: "Papua New Guinea", currency: "PGK" },
        "PY": { name: "Paraguay", currency: "PYG" },
        "PE": { name: "Peru", currency: "PEN" },
        "PH": { name: "Philippines", currency: "PHP" },
        "PN": { name: "Pitcairn Islands", currency: "NZD" },
        "PL": { name: "Poland", currency: "PLN" },
        "PT": { name: "Portugal", currency: "EUR" },
        "PR": { name: "Puerto Rico", currency: "USD" },
        "QA": { name: "Qatar", currency: "QAR" },
        "RE": { name: "Réunion", currency: "EUR" },
        "RO": { name: "Romania", currency: "RON" },
        "RU": { name: "Russia", currency: "RUB" },
        "RW": { name: "Rwanda", currency: "RWF" },
        "WS": { name: "Samoa", currency: "WST" },
        "SM": { name: "San Marino", currency: "EUR" },
        "ST": { name: "São Tomé & Príncipe", currency: "STD" },
        "SA": { name: "Saudi Arabia", currency: "SAR" },
        "SN": { name: "Senegal", currency: "XOF" },
        "RS": { name: "Serbia", currency: "RSD" },
        "SC": { name: "Seychelles", currency: "SCR" },
        "SL": { name: "Sierra Leone", currency: "SLL" },
        "SG": { name: "Singapore", currency: "SGD" },
        "SX": { name: "Sint Maarten", currency: "ANG" },
        "SK": { name: "Slovakia", currency: "EUR" },
        "SI": { name: "Slovenia", currency: "EUR" },
        "SB": { name: "Solomon Islands", currency: "SBD" },
        "SO": { name: "Somalia", currency: "SOS" },
        "ZA": { name: "South Africa", currency: "ZAR" },
        "GS": { name: "South Georgia & South Sandwich Islands", currency: "GBP" },
        "KR": { name: "South Korea", currency: "KRW" },
        "SS": { name: "South Sudan", currency: "SSP" },
        "ES": { name: "Spain", currency: "EUR" },
        "LK": { name: "Sri Lanka", currency: "LKR" },
        "BL": { name: "St. Barthélemy", currency: "EUR" },
        "SH": { name: "St. Helena", currency: "SHP" },
        "KN": { name: "St. Kitts & Nevis", currency: "XCD" },
        "LC": { name: "St. Lucia", currency: "XCD" },
        "MF": { name: "St. Martin", currency: "EUR" },
        "PM": { name: "St. Pierre & Miquelon", currency: "EUR" },
        "VC": { name: "St. Vincent & Grenadines", currency: "XCD" },
        "SD": { name: "Sudan", currency: "SDG" },
        "SR": { name: "Suriname", currency: "SRD" },
        "SJ": { name: "Svalbard & Jan Mayen", currency: "NOK" },
        "SE": { name: "Sweden", currency: "SEK" },
        "CH": { name: "Switzerland", currency: "CHF" },
        "SY": { name: "Syria", currency: "SYP" },
        "TW": { name: "Taiwan", currency: "TWD" },
        "TJ": { name: "Tajikistan", currency: "TJS" },
        "TH": { name: "Thailand", currency: "THB" },
        "TL": { name: "Timor-Leste", currency: "USD" },
        "TG": { name: "Togo", currency: "XOF" },
        "TK": { name: "Tokelau", currency: "NZD" },
        "TO": { name: "Tonga", currency: "TOP" },
        "TT": { name: "Trinidad & Tobago", currency: "TTD" },
        "TN": { name: "Tunisia", currency: "TND" },
        "TR": { name: "Türkiye", currency: "TRY" },
        "TM": { name: "Turkmenistan", currency: "TMT" },
        "TC": { name: "Turks & Caicos Islands", currency: "USD" },
        "TV": { name: "Tuvalu", currency: "AUD" },
        "UM": { name: "U.S. Outlying Islands", currency: "USD" },
        "VI": { name: "U.S. Virgin Islands", currency: "USD" },
        "UG": { name: "Uganda", currency: "UGX" },
        "UA": { name: "Ukraine", currency: "UAH" },
        "AE": { name: "United Arab Emirates", currency: "AED" },
        "GB": { name: "United Kingdom", currency: "GBP" },
        "UY": { name: "Uruguay", currency: "UYU" },
        "UZ": { name: "Uzbekistan", currency: "UZS" },
        "VU": { name: "Vanuatu", currency: "VUV" },
        "VA": { name: "Vatican City", currency: "EUR" },
        "VE": { name: "Venezuela", currency: "VES" },
        "VN": { name: "Vietnam", currency: "VND" },
        "WF": { name: "Wallis & Futuna", currency: "XPF" },
        "EH": { name: "Western Sahara", currency: "MAD" },
        "YE": { name: "Yemen", currency: "YER" },
        "ZM": { name: "Zambia", currency: "ZMW" },
        "ZW": { name: "Zimbabwe", currency: "ZWL" }
    };

    // Extract country codes for mapping
    const countries = Object.keys(countryData);

    // Update paymentCurrency when senderCountry changes
    const handleSenderCountryChange = (e) => {
        const selectedCountryCode = e.target.value;
        const newCurrency = countryData[selectedCountryCode]?.currency || 'CAD'; // Default to CAD
        setFormState(prevState => ({
            ...prevState,
            senderCountry: selectedCountryCode,
            paymentCurrency: newCurrency,
        }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8 font-inter">
            <h1 className="text-5xl font-extrabold text-center text-blue-800 mb-12 shadow-text">
                Blue Lane Express
            </h1>

            <form onSubmit={handleCalculateShipping} className="max-w-4xl mx-auto bg-white p-10 rounded-3xl shadow-2xl space-y-8">
                {/* Sender Information */}
                <section className="border-b-2 border-blue-200 pb-8">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6">Sender Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="senderName" className="block text-gray-700 text-sm font-bold mb-2">Name: <span className="text-red-500">*</span></label>
                            <input type="text" id="senderName" value={senderName} onChange={(e) => setFormState(prevState => ({ ...prevState, senderName: e.target.value }))} required className="form-input" placeholder="e.g., Jane Doe" />
                        </div>
                        <div>
                            <label htmlFor="senderEmail" className="block text-gray-700 text-sm font-bold mb-2">Email: <span className="text-red-500">*</span></label>
                            <input type="email" id="senderEmail" value={senderEmail} onChange={(e) => setFormState(prevState => ({ ...prevState, senderEmail: e.target.value }))} required className="form-input" placeholder="e.g., jane.doe@example.com" />
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="senderAddress" className="block text-gray-700 text-sm font-bold mb-2">Address: <span className="text-red-500">*</span></label>
                            <input type="text" id="senderAddress" value={senderAddress} onChange={(e) => setFormState(prevState => ({ ...prevState, senderAddress: e.target.value }))} required className="form-input" placeholder="e.g., 123 Main Street" />
                        </div>
                        <div>
                            <label htmlFor="senderSuite" className="block text-gray-700 text-sm font-bold mb-2">Suite/Apt:</label>
                            <input type="text" id="senderSuite" value={senderSuite} onChange={(e) => setFormState(prevState => ({ ...prevState, senderSuite: e.target.value }))} className="form-input" placeholder="e.g., Suite 100" />
                        </div>
                        <div>
                            <label htmlFor="senderDepartment" className="block text-gray-700 text-sm font-bold mb-2">Department:</label>
                            <input type="text" id="senderDepartment" value={senderDepartment} onChange={(e) => setFormState(prevState => ({ ...prevState, senderDepartment: e.target.value }))} className="form-input" placeholder="e.g., Shipping Dept" />
                        </div>
                        <div>
                            <label htmlFor="senderCity" className="block text-gray-700 text-sm font-bold mb-2">City: <span className="text-red-500">*</span></label>
                            <input type="text" id="senderCity" value={senderCity} onChange={(e) => setFormState(prevState => ({ ...prevState, senderCity: e.target.value }))} required className="form-input" placeholder="e.g., Anytown" />
                        </div>
                        <div>
                            <label htmlFor="senderCountry" className="block text-gray-700 text-sm font-bold mb-2">Country: <span className="text-red-500">*</span></label>
                            <select id="senderCountry" value={senderCountry} onChange={handleSenderCountryChange} required className="form-select">
                                <option value="">Select Country</option> {/* Added "Select Country" option */}
                                {countries.map(countryCode => (
                                    <option key={countryCode} value={countryCode}>{countryData[countryCode].name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="senderProvince" className="block text-gray-700 text-sm font-bold mb-2">Province/State: <span className="text-red-500">*</span></label>
                            {(senderCountry === 'CA' || senderCountry === 'US') ? (
                                <select id="senderProvince" value={senderProvince} onChange={(e) => setFormState(prevState => ({ ...prevState, senderProvince: e.target.value }))} required className="form-select">
                                    <option value="">Select Province/State</option>
                                    {senderCountry === 'CA' && provinces.map(province => (
                                        <option key={province} value={province}>{province}</option>
                                    ))}
                                    {senderCountry === 'US' && usStates.map(state => (
                                        <option key={state.code} value={state.name}>{state.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input type="text" id="senderProvince" value={senderProvince} onChange={(e) => setFormState(prevState => ({ ...prevState, senderProvince: e.target.value }))} className="form-input" placeholder="Enter Province/State" />
                            )}
                        </div>
                        <div>
                            <label htmlFor="senderPostalCode" className="block text-gray-700 text-sm font-bold mb-2">Postal Code: <span className="text-red-500">*</span></label>
                            <input type="text" id="senderPostalCode" value={senderPostalCode} onChange={(e) => setFormState(prevState => ({ ...prevState, senderPostalCode: e.target.value }))} className="form-input" placeholder="e.g., A1A 1A1" />
                        </div>
                        <div>
                            <label htmlFor="senderPhone" className="block text-gray-700 text-sm font-bold mb-2">Phone: <span className="text-red-500">*</span></label>
                            <input type="tel" id="senderPhone" value={senderPhone} onChange={(e) => setFormState(prevState => ({ ...prevState, senderPhone: e.target.value }))} required className="form-input" placeholder="e.g., 555-111-2222" />
                        </div>
                        <div>
                            <label htmlFor="senderExt" className="block text-gray-700 text-sm font-bold mb-2">Ext:</label>
                            <input type="text" id="senderExt" value={senderExt} onChange={(e) => setFormState(prevState => ({ ...prevState, senderExt: e.target.value }))} className="form-input" placeholder="e.g., 123" />
                        </div>
                        <div className="md:col-span-2 flex items-center">
                            <input type="checkbox" id="senderIsCommercial" checked={senderIsCommercial} onChange={(e) => setFormState(prevState => ({ ...prevState, senderIsCommercial: e.target.checked }))} className="mr-2" />
                            <label htmlFor="senderIsCommercial" className="text-gray-700 text-sm font-bold">Commercial Address</label>
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="senderAttn" className="block text-gray-700 text-sm font-bold mb-2">Attention To: <span className="text-red-500">*</span></label>
                            <input type="text" id="senderAttn" value={senderAttn} onChange={(e) => setFormState(prevState => ({ ...prevState, senderAttn: e.target.value }))} required className="form-input" placeholder="e.g., Shipping Dept" />
                        </div>
                    </div>
                </section>

                {/* Receiver Information */}
                <section className="border-b-2 border-blue-200 pb-8">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6">Receiver Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="receiverName" className="block text-gray-700 text-sm font-bold mb-2">Name: <span className="text-red-500">*</span></label>
                            <input type="text" id="receiverName" value={receiverName} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverName: e.target.value }))} required className="form-input" placeholder="e.g., John Smith" />
                        </div>
                        <div>
                            <label htmlFor="receiverEmail" className="block text-gray-700 text-sm font-bold mb-2">Email: <span className="text-red-500">*</span></label>
                            <input type="email" id="receiverEmail" value={receiverEmail} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverEmail: e.target.value }))} required className="form-input" placeholder="e.g., john.smith@example.com" />
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="receiverAddress" className="block text-gray-700 text-sm font-bold mb-2">Address: <span className="text-red-500">*</span></label>
                            <input type="text" id="receiverAddress" value={receiverAddress} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverAddress: e.target.value }))} required className="form-input" placeholder="e.g., 456 Oak Avenue" />
                        </div>
                        <div>
                            <label htmlFor="receiverSuite" className="block text-gray-700 text-sm font-bold mb-2">Suite/Apt:</label>
                            <input type="text" id="receiverSuite" value={receiverSuite} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverSuite: e.target.value }))} className="form-input" placeholder="e.g., Unit 200" />
                        </div>
                        <div>
                            <label htmlFor="receiverDepartment" className="block text-gray-700 text-sm font-bold mb-2">Department:</label>
                            <input type="text" id="receiverDepartment" value={receiverDepartment} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverDepartment: e.target.value }))} className="form-input" placeholder="e.g., Receiving Dept" />
                        </div>
                        <div>
                            <label htmlFor="receiverCity" className="block text-gray-700 text-sm font-bold mb-2">City: <span className="text-red-500">*</span></label>
                            <input type="text" id="receiverCity" value={receiverCity} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverCity: e.target.value }))} required className="form-input" placeholder="e.g., Otherville" />
                        </div>
                        <div>
                            <label htmlFor="receiverCountry" className="block text-gray-700 text-sm font-bold mb-2">Country: <span className="text-red-500">*</span></label>
                            <select id="receiverCountry" value={receiverCountry} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverCountry: e.target.value }))} required className="form-select">
                                <option value="">Select Country</option> {/* Added "Select Country" option */}
                                {countries.map(countryCode => (
                                    <option key={countryCode} value={countryCode}>{countryData[countryCode].name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="receiverProvince" className="block text-gray-700 text-sm font-bold mb-2">Province/State: <span className="text-red-500">*</span></label>
                            {(receiverCountry === 'CA' || receiverCountry === 'US') ? (
                                <select id="receiverProvince" value={receiverProvince} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverProvince: e.target.value }))} required className="form-select">
                                    <option value="">Select Province/State</option>
                                    {receiverCountry === 'CA' && provinces.map(province => (
                                        <option key={province} value={province}>{province}</option>
                                    ))}
                                    {receiverCountry === 'US' && usStates.map(state => (
                                        <option key={state.code} value={state.name}>{state.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input type="text" id="receiverProvince" value={receiverProvince} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverProvince: e.target.value }))} className="form-input" placeholder="Enter Province/State" />
                            )}
                        </div>
                        <div>
                            <label htmlFor="receiverPostalCode" className="block text-gray-700 text-sm font-bold mb-2">Postal Code: <span className="text-red-500">*</span></label>
                            <input type="text" id="receiverPostalCode" value={receiverPostalCode} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverPostalCode: e.target.value }))} className="form-input" placeholder="e.g., B2B 2B2" />
                        </div>
                        <div>
                            <label htmlFor="receiverPhone" className="block text-gray-700 text-sm font-bold mb-2">Phone: <span className="text-red-500">*</span></label>
                            <input type="tel" id="receiverPhone" value={receiverPhone} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverPhone: e.target.value }))} required className="form-input" placeholder="e.g., 555-333-4444" />
                        </div>
                        <div>
                            <label htmlFor="receiverExt" className="block text-gray-700 text-sm font-bold mb-2">Ext:</label>
                            <input type="text" id="receiverExt" value={receiverExt} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverExt: e.target.value }))} className="form-input" placeholder="e.g., 456" />
                        </div>
                        <div className="md:col-span-2 flex items-center">
                            <input type="checkbox" id="receiverIsCommercial" checked={receiverIsCommercial} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverIsCommercial: e.target.checked }))} className="mr-2" />
                            <label htmlFor="receiverIsCommercial" className="text-gray-700 text-sm font-bold">Commercial Address</label>
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="receiverAttn" className="block text-gray-700 text-sm font-bold mb-2">Attention To: <span className="text-red-500">*</span></label>
                            <input type="text" id="receiverAttn" value={receiverAttn} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverAttn: e.target.value }))} required className="form-input" placeholder="e.g., Receiving Dept" />
                        </div>
                    </div>
                </section>

                {/* Package Information */}
                <section className="border-b-2 border-blue-200 pb-8">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6">Package Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="packageWeight" className="block text-gray-700 text-sm font-bold mb-2">Weight ({packageUnits === 'imperial' ? 'lbs' : 'kg'}): <span className="text-red-500">*</span></label>
                            <input type="number" id="packageWeight" value={packageWeight} onChange={(e) => setFormState(prevState => ({ ...prevState, packageWeight: e.target.value }))} required className="form-input" step="0.01" placeholder="e.g., 4" />
                        </div>
                        <div>
                            <label htmlFor="packageLength" className="block text-gray-700 text-sm font-bold mb-2">Length ({packageUnits === 'imperial' ? 'in' : 'cm'}): <span className="text-red-500">*</span></label>
                            <input type="number" id="packageLength" value={packageLength} onChange={(e) => setFormState(prevState => ({ ...prevState, packageLength: e.target.value }))} required className="form-input" step="0.01" placeholder="e.g., 4" />
                        </div>
                        <div>
                            <label htmlFor="packageWidth" className="block text-gray-700 text-sm font-bold mb-2">Width ({packageUnits === 'imperial' ? 'in' : 'cm'}): <span className="text-red-500">*</span></label>
                            <input type="number" id="packageWidth" value={packageWidth} onChange={(e) => setFormState(prevState => ({ ...prevState, packageWidth: e.target.value }))} required className="form-input" step="0.01" placeholder="e.g., 4" />
                        </div>
                        <div>
                            <label htmlFor="packageHeight" className="block text-gray-700 text-sm font-bold mb-2">Height ({packageUnits === 'imperial' ? 'in' : 'cm'}): <span className="text-red-500">*</span></label>
                            <input type="number" id="packageHeight" value={packageHeight} onChange={(e) => setFormState(prevState => ({ ...prevState, packageHeight: e.target.value }))} required className="form-input" step="0.01" placeholder="e.g., 4" />
                        </div>
                        <div>
                            <label htmlFor="packageUnits" className="block text-gray-700 text-sm font-bold mb-2">Units: <span className="text-red-500">*</span></label>
                            <select id="packageUnits" value={packageUnits} onChange={(e) => setFormState(prevState => ({ ...prevState, packageUnits: e.target.value }))} required className="form-select">
                                <option value="imperial">Imperial (lbs, in)</option>
                                <option value="metric">Metric (kg, cm)</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="packageType" className="block text-gray-700 text-sm font-bold mb-2">Package Type: <span className="text-red-500">*</span></label>
                            <select id="packageType" value={packageType} onChange={(e) => setFormState(prevState => ({ ...prevState, packageType: e.target.value }))} required className="form-select">
                                <option value="package">Package</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="packageContent" className="block text-gray-700 text-sm font-bold mb-2">Content Type: <span className="text-red-500">*</span></label>
                            <select id="packageContent" value={packageContent} onChange={(e) => setFormState(prevState => ({ ...prevState, packageContent: e.target.value }))} required className="form-select">
                                <option value="goods">Goods</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="packageDescription" className="block text-gray-700 text-sm font-bold mb-2">Package Description: <span className="text-red-500">*</span></label>
                            <input type="text" id="packageDescription" value={packageDescription} onChange={(e) => setFormState(prevState => ({ ...prevState, packageDescription: e.target.value }))} required className="form-input" placeholder="e.g., General Goods" />
                        </div>
                    </div>
                </section>

                {/* Optional Services */}
                <section className="border-b-2 border-blue-200 pb-8">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6">Optional Services & Preferences</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-center">
                            <input type="checkbox" id="signatureRequired" checked={signatureRequired} onChange={(e) => setFormState(prevState => ({ ...prevState, signatureRequired: e.target.checked }))} className="mr-2" />
                            <label htmlFor="signatureRequired" className="text-gray-700 text-sm font-bold">Signature Required</label>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" id="saturdayDelivery" checked={saturdayDelivery} onChange={(e) => setFormState(prevState => ({ ...prevState, saturdayDelivery: e.target.checked }))} className="mr-2" />
                            <label htmlFor="saturdayDelivery" className="text-gray-700 text-sm font-bold">Saturday Delivery</label>
                        </div>
                        <div>
                            <label htmlFor="shippingDate" className="block text-gray-700 text-sm font-bold mb-2">Shipping Date (YYYY-MM-DD): <span className="text-red-500">*</span></label>
                            <input type="date" id="shippingDate" value={shippingDate} onChange={(e) => setFormState(prevState => ({ ...prevState, shippingDate: e.target.value }))} required className="form-input" />
                        </div>
                        <div>
                            <label htmlFor="reference" className="block text-gray-700 text-sm font-bold mb-2">Reference:</label>
                            <input type="text" id="reference" value={reference} onChange={(e) => setFormState(prevState => ({ ...prevState, reference: e.target.value }))} className="form-input" placeholder="e.g., PO12345" />
                        </div>
                        <div className="md:col-span-2">
                            <label htmlFor="driverInstructions" className="block text-gray-700 text-sm font-bold mb-2">Driver Instructions:</label>
                            <textarea id="driverInstructions" value={driverInstructions} onChange={(e) => setFormState(prevState => ({ ...prevState, driverInstructions: e.target.value }))} className="form-input" placeholder="e.g., Leave at front desk"></textarea>
                        </div>
                        <div>
                            <label htmlFor="shipmentTrackingEmails" className="block text-gray-700 text-sm font-bold mb-2">Tracking Emails (comma-separated):</label>
                            <input type="text" id="shipmentTrackingEmails" value={shipmentTrackingEmails} onChange={(e) => setFormState(prevState => ({ ...prevState, shipmentTrackingEmails: e.target.value }))} className="form-input" placeholder="email1@example.com, email2@example.com" />
                        </div>
                        <div>
                            <label htmlFor="paymentCurrency" className="block text-gray-700 text-sm font-bold mb-2">Payment Currency: <span className="text-red-500">*</span></label>
                            <select id="paymentCurrency" value={paymentCurrency} onChange={(e) => setFormState(prevState => ({ ...prevState, paymentCurrency: e.target.value }))} required className="form-select">
                                {/* Filter to only show CAD, USD, EUR */}
                                {['CAD', 'USD', 'EUR'].map(currencyCode => (
                                    <option key={currencyCode} value={currencyCode}>{currencyCode}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </section>

                {/* International Shipment Details - This section is now conditionally rendered */}
                {isInternationalShipment && (
                    <section className="border-b-2 border-blue-200 pb-8">
                        <h2 className="text-3xl font-bold text-blue-700 mb-6">International Shipment Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Sold To Address */}
                            <div className="md:col-span-2 border rounded-lg p-4 bg-blue-50">
                                <h3 className="text-lg font-semibold text-blue-800 mb-3">Sold To Address</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="soldToName" className="block text-gray-700 text-sm font-bold mb-2">Name: <span className="text-red-500">*</span></label>
                                        <input type="text" id="soldToName" value={soldToName} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToName: e.target.value }))} required className="form-input" placeholder="e.g., John Doe" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToAttn" className="block text-gray-700 text-sm font-bold mb-2">Attention To: <span className="text-red-500">*</span></label> {/* Made required */}
                                        <input type="text" id="soldToAttn" value={soldToAttn} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToAttn: e.target.value }))} required className="form-input" placeholder="e.g., Accounts Dept" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label htmlFor="soldToAddress" className="block text-gray-700 text-sm font-bold mb-2">Address: <span className="text-red-500">*</span></label>
                                        <input type="text" id="soldToAddress" value={soldToAddress} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToAddress: e.target.value }))} required className="form-input" placeholder="e.g., 123 Main St" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToSuite" className="block text-gray-700 text-sm font-bold mb-2">Suite/Apt:</label>
                                        <input type="text" id="soldToSuite" value={soldToSuite} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToSuite: e.target.value }))} className="form-input" placeholder="e.g., Suite 300" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToCity" className="block text-gray-700 text-sm font-bold mb-2">City: <span className="text-red-500">*</span></label>
                                        <input type="text" id="soldToCity" value={soldToCity} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToCity: e.target.value }))} required className="form-input" placeholder="e.g., New York" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToCountry" className="block text-gray-700 text-sm font-bold mb-2">Country: <span className="text-red-500">*</span></label>
                                        <select id="soldToCountry" value={soldToCountry} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToCountry: e.target.value }))} required className="form-select">
                                            <option value="">Select Country</option> {/* Added "Select Country" option */}
                                            {countries.map(countryCode => (
                                                <option key={countryCode} value={countryCode}>{countryData[countryCode].name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="soldToState" className="block text-gray-700 text-sm font-bold mb-2">Province/State: <span className="text-red-500">*</span></label>
                                        {(soldToCountry === 'CA' || soldToCountry === 'US') ? (
                                            <select id="soldToState" value={soldToState} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToState: e.target.value }))} required className="form-select">
                                                <option value="">Select Province/State</option>
                                                {soldToCountry === 'CA' && provinces.map(province => (
                                                    <option key={province} value={province}>{province}</option>
                                                ))}
                                                {soldToCountry === 'US' && usStates.map(state => (
                                                    <option key={state.code} value={state.name}>{state.name}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input type="text" id="soldToState" value={soldToState} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToState: e.target.value }))} required={true} className="form-input" placeholder="Enter Province/State" />
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="soldToPostalCode" className="block text-gray-700 text-sm font-bold mb-2">Postal Code: <span className="text-red-500">*</span></label>
                                        <input type="text" id="soldToPostalCode" value={soldToPostalCode} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToPostalCode: e.target.value }))} required className="form-input" placeholder="e.g., 10001" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToPhone" className="block text-gray-700 text-sm font-bold mb-2">Phone: <span className="text-red-500">*</span></label>
                                        <input type="tel" id="soldToPhone" value={soldToPhone} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToPhone: e.target.value }))} required className="form-input" placeholder="e.g., 555-123-4567" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToExt" className="block text-gray-700 text-sm font-bold mb-2">Ext:</label>
                                        <input type="text" id="soldToExt" value={soldToExt} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToExt: e.target.value }))} className="form-input" placeholder="e.g., 789" />
                                    </div>
                                    <div>
                                        <label htmlFor="soldToDepartment" className="block text-gray-700 text-sm font-bold mb-2">Department:</label>
                                        <input type="text" id="soldToDepartment" value={soldToDepartment} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToDepartment: e.target.value }))} className="form-input" placeholder="e.g., Sales" />
                                    </div>
                                    <div className="md:col-span-2 flex items-center">
                                        <input type="checkbox" id="soldToIsCommercial" checked={soldToIsCommercial} onChange={(e) => setFormState(prevState => ({ ...prevState, soldToIsCommercial: e.target.checked }))} className="mr-2" />
                                        <label htmlFor="soldToIsCommercial" className="text-gray-700 text-sm font-bold">Commercial Address</label>
                                    </div>
                                </div>
                            </div>

                            {/* Duties Payer */}
                            <div className="md:col-span-2">
                                <label htmlFor="dutiesPayer" className="block text-gray-700 text-sm font-bold mb-2">Duties Payer: <span className="text-red-500">*</span></label>
                                <select id="dutiesPayer" value={dutiesPayer} onChange={(e) => setFormState(prevState => ({ ...prevState, dutiesPayer: e.target.value }))} required className="form-select">
                                    {/* Only 'Receiver' option, sending 'C' to backend */}
                                    <option value="C">Receiver</option>
                                </select>
                            </div>
                            {dutiesPayer === 'T' && ( // This block will now effectively never show
                                <div className="md:col-span-2">
                                    <label htmlFor="dutiesPayerAccount" className="block text-gray-700 text-sm font-bold mb-2">Duties Payer Account: <span className="text-red-500">*</span></label>
                                    <input type="text" id="dutiesPayerAccount" value={dutiesPayerAccount} onChange={(e) => setFormState(prevState => ({ ...prevState, dutiesPayerAccount: e.target.value }))} required className="form-input" placeholder="e.g., ABC12345" />
                                </div>
                            )}
                            <div>
                                <label htmlFor="reasonForExport" className="block text-gray-700 text-sm font-bold mb-2">Reason for Export: <span className="text-red-500">*</span></label>
                                <select id="reasonForExport" value={reasonForExport} onChange={(e) => setFormState(prevState => ({ ...prevState, reasonForExport: e.target.value }))} required className="form-select">
                                    <option value="P">Permanent (Sale of Goods)</option>
                                    <option value="T">Temporary</option>
                                    <option value="R">Return</option>
                                    <option value="G">Gift</option>
                                    <option value="S">Sample</option>
                                </select>
                            </div>
                            <div className="flex items-center">
                                <input type="checkbox" id="isNafta" checked={isNafta} onChange={(e) => setFormState(prevState => ({ ...prevState, isNafta: e.target.checked }))} className="mr-2" />
                                <label htmlFor="isNafta" className="text-gray-700 text-sm font-bold">NAFTA Compliant</label>
                            </div>

                            {/* Inquiry Details */}
                            <div className="md:col-span-2 border rounded-lg p-4 bg-blue-50">
                                <h3 className="text-lg font-semibold text-blue-800 mb-3">Inquiry Details <span className="text-red-500">*</span></h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label htmlFor="inquiryCompany" className="block text-gray-700 text-sm font-bold mb-2">Company: <span className="text-red-500">*</span></label>
                                        <input type="text" id="inquiryCompany" value={inquiryCompany} onChange={(e) => setFormState(prevState => ({ ...prevState, inquiryCompany: e.target.value }))} required={isInternationalShipment} className="form-input" placeholder="e.g., Global Imports" />
                                    </div>
                                    <div>
                                        <label htmlFor="inquiryName" className="block text-gray-700 text-sm font-bold mb-2">Name: <span className="text-red-500">*</span></label>
                                        <input type="text" id="inquiryName" value={inquiryName} onChange={(e) => setFormState(prevState => ({ ...prevState, inquiryName: e.target.value }))} required={isInternationalShipment} className="form-input" placeholder="e.g., Jane Smith" />
                                    </div>
                                    <div>
                                        <label htmlFor="brokerId" className="block text-gray-700 text-sm font-bold mb-2">Broker ID:</label>
                                        <input type="text" id="brokerId" value={brokerId} onChange={(e) => setFormState(prevState => ({ ...prevState, brokerId: e.target.value }))} className="form-input" placeholder="e.g., BRK987" />
                                    </div>
                                    <div>
                                        <label htmlFor="senderTaxId" className="block text-gray-700 text-sm font-bold mb-2">Sender Tax ID:</label>
                                        <input type="text" id="senderTaxId" value={senderTaxId} onChange={(e) => setFormState(prevState => ({ ...prevState, senderTaxId: e.target.value }))} className="form-input" placeholder="e.g., SENDERTAX123" />
                                    </div>
                                    <div>
                                        <label htmlFor="receiverTaxId" className="block text-gray-700 text-sm font-bold mb-2">Receiver Tax ID:</label>
                                        <input type="text" id="receiverTaxId" value={receiverTaxId} onChange={(e) => setFormState(prevState => ({ ...prevState, receiverTaxId: e.target.value }))} className="form-input" placeholder="e.g., RECEIVERTAX456" />
                                    </div>
                                    <div>
                                        <label htmlFor="inquiryPhone" className="block text-gray-700 text-sm font-bold mb-2">Inquiry Phone: <span className="text-red-500">*</span></label>
                                        <input type="tel" id="inquiryPhone" value={inquiryPhone} onChange={(e) => setFormState(prevState => ({ ...prevState, inquiryPhone: e.target.value }))} required={isInternationalShipment} className="form-input" placeholder="e.g., 555-987-6543" />
                                    </div>
                                    <div>
                                        <label htmlFor="inquiryPhoneExt" className="block text-gray-700 text-sm font-bold mb-2">Inquiry Phone Ext:</label>
                                        <input type="text" id="inquiryPhoneExt" value={inquiryPhoneExt} onChange={(e) => setFormState(prevState => ({ ...prevState, inquiryPhoneExt: e.target.value }))} className="form-input" placeholder="e.g., 321" />
                                    </div>
                                </div>
                            </div>

                            {/* Declared Items */}
                            <div className="md:col-span-2 border rounded-lg p-4 bg-blue-50">
                                <h3 className="text-lg font-semibold text-blue-800 mb-3">Declared Items <span className="text-red-500">*</span></h3>
                                {declaredItems.map((item, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
                                        <div>
                                            <label htmlFor={`product_name_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Product Name: <span className="text-red-500">*</span></label>
                                            <input type="text" id={`product_name_${index}`} value={item.product_name} onChange={(e) => handleDeclaredItemChange(index, 'product_name', e.target.value)} required className="form-input" placeholder="e.g., Laptop" />
                                        </div>
                                        <div>
                                            <label htmlFor={`description_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Description: <span className="text-red-500">*</span></label>
                                            <input type="text" id={`description_${index}`} value={item.description} onChange={(e) => handleDeclaredItemChange(index, 'description', e.target.value)} required className="form-input" placeholder="e.g., Dell XPS 15" />
                                        </div>
                                        <div>
                                            <label htmlFor={`HS_code_${index}`} className="block text-gray-700 text-sm font-bold mb-2">HS Code:</label>
                                            <input type="text" id={`HS_code_${index}`} value={item.HS_code} onChange={(e) => handleDeclaredItemChange(index, 'HS_code', e.target.value)} className="form-input" placeholder="e.g., 8471.30.0000" />
                                        </div>
                                        <div>
                                            <label htmlFor={`country_of_origin_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Country of Origin: <span className="text-red-500">*</span></label>
                                            <select id={`country_of_origin_${index}`} value={item.country_of_origin} onChange={(e) => handleDeclaredItemChange(index, 'country_of_origin', e.target.value)} required className="form-select">
                                                <option value="">Select Country</option> {/* Added "Select Country" option */}
                                                {countries.map(countryCode => (
                                                    <option key={countryCode} value={countryCode}>{countryData[countryCode].name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor={`quantity_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Quantity: <span className="text-red-500">*</span></label>
                                            <input type="number" id={`quantity_${index}`} value={item.quantity} onChange={(e) => handleDeclaredItemChange(index, 'quantity', e.target.value)} required className="form-input" min="1" placeholder="e.g., 1" />
                                        </div>
                                        <div>
                                            <label htmlFor={`unit_price_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Unit Price: <span className="text-red-500">*</span></label>
                                            <input type="number" id={`unit_price_${index}`} value={item.unit_price} onChange={(e) => handleDeclaredItemChange(index, 'unit_price', e.target.value)} required className="form-input" step="0.01" min="0" placeholder="e.g., 1200.00" />
                                        </div>
                                        <div>
                                            <label htmlFor={`unit_weight_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Unit Weight ({packageUnits === 'imperial' ? 'lbs' : 'kg'}): <span className="text-red-500">*</span></label>
                                            <input type="number" id={`unit_weight_${index}`} value={item.unit_weight} onChange={(e) => handleDeclaredItemChange(index, 'unit_weight', e.target.value)} required className="form-input" step="0.01" min="0" placeholder="e.g., 2.5" />
                                        </div>
                                        <div>
                                            <label htmlFor={`unit_of_measurement_${index}`} className="block text-gray-700 text-sm font-bold mb-2">Unit of Measurement: <span className="text-red-500">*</span></label>
                                            <select id={`unit_of_measurement_${index}`} value={item.unit_of_measurement} onChange={(e) => handleDeclaredItemChange(index, 'unit_of_measurement', e.target.value)} required className="form-select">
                                                <option value="pounds">Pounds</option>
                                                <option value="kilograms">Kilograms</option>
                                            </select>
                                        </div>
                                        {declaredItems.length > 1 && (
                                            <button type="button" onClick={() => handleRemoveDeclaredItem(index)} className="md:col-span-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200 ease-in-out">
                                                Remove Item
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button type="button" onClick={handleAddDeclaredItem} className="mt-4 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200 ease-in-out">
                                    Add Declared Item
                                </button>
                            </div>
                        </div>
                    </section>
                )}

                <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 ease-in-out text-xl">
                    {loading ? 'Calculating...' : 'Calculate Shipping Rates'}
                </button>
            </form>

            {error && (
                <div className="max-w-4xl mx-auto mt-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg shadow-md" role="alert">
                    <p className="font-bold">Error!</p>
                    <p>{error}</p>
                </div>
            )}

            {loading && (
                <div className="max-w-4xl mx-auto mt-8 p-4 text-center text-blue-700 text-lg">
                    Loading shipping options...
                </div>
            )}

            {shippingOptions.length > 0 && !selectedService && (
                <div className="max-w-4xl mx-auto mt-8 p-8 bg-white rounded-3xl shadow-2xl">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6 text-center">Available Shipping Options</h2>
                    <p className="text-gray-600 text-center mb-6">Distance: {distance ? `${distance.toFixed(2)} KM` : 'N/A'}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {shippingOptions.map((option, index) => (
                            <div key={index} className="bg-blue-50 p-6 rounded-xl shadow-lg border border-blue-200 flex flex-col justify-between">
                                <div>
                                    <h3 className="text-xl font-semibold text-blue-800 mb-2">{option.serviceDescription}</h3>
                                    <p className="text-gray-700 mb-1">Courier: {option.courierName}</p>
                                    <p className="text-gray-700 mb-1">Shipping Cost: ${option.shippingCost.toFixed(2)}</p>
                                    <p className="text-gray-700 mb-1">Commission ({option.commissionPercentage * 100}%): ${option.commissionAmount.toFixed(2)}</p>
                                    {/* Corrected access to estimated_delivery_date */}
                                    <p className="text-gray-700 mb-1">Est. Delivery: {option.fullServiceDetails?.estimated_delivery_date || 'N/A'}</p>
                                </div>
                                <div className="mt-4">
                                    <p className="text-2xl font-extrabold text-blue-700">Total: ${option.totalCost.toFixed(2)}</p>
                                    <button onClick={() => handleServiceSelect(option)} className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200 ease-in-out">
                                        Select Service
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {selectedService && currentOrderData && (
                <div className="max-w-4xl mx-auto mt-8 p-8 bg-white rounded-3xl shadow-2xl">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6 text-center">Confirm Your Order</h2>
                    <div className="bg-blue-50 p-6 rounded-xl shadow-inner mb-6">
                        <h3 className="text-xl font-semibold text-blue-800 mb-2">Selected Service: {selectedService.serviceDescription}</h3>
                        <p className="text-gray-700 mb-1">Courier: {selectedService.courierName}</p>
                        <p className="text-gray-700 mb-1">Shipping Cost: ${selectedService.shippingCost.toFixed(2)}</p>
                        <p className="text-700 mb-1">Commission: ${selectedService.commissionAmount.toFixed(2)}</p>
                        <p className="text-2xl font-extrabold text-blue-700">Total Amount: ${selectedService.totalCost.toFixed(2)}</p>
                    </div>
                    {clientSecret && stripeClient ? ( // Ensure both clientSecret and stripeClient are available
                        <Elements options={{ clientSecret }} stripe={stripeClient}> {/* Pass clientSecret to Elements options */}
                            <CheckoutForm
                                orderData={currentOrderData}
                                backendUrl={backendUrl}
                                onPaymentSuccess={handlePaymentSuccess}
                                onPaymentError={handlePaymentError}
                                selectedService={selectedService}
                            />
                        </Elements>
                    ) : (
                        <p className="text-gray-600 mt-4">Loading payment form...</p>
                    )}
                </div>
            )}

            {labelDetails && (
                <div className="max-w-4xl mx-auto mt-8 p-8 bg-white rounded-3xl shadow-2xl">
                    <h2 className="text-3xl font-bold text-blue-700 mb-6 text-center">Shipping Label Generated!</h2>
                    <div className="bg-green-50 p-6 rounded-xl shadow-inner mb-6 text-center">
                        <p className="text-green-800 text-xl font-semibold mb-2">Tracking Number: {labelDetails.trackingNumber}</p>
                        {labelDetails.message && <p className="text-green-700 text-sm mb-4">{labelDetails.message}</p>}

                        {labelDetails.labelUrl ? (
                            <div className="relative">
                                {/* Conditional rendering based on isLabelDisplayReady */}
                                {!isLabelDisplayReady ? (
                                    <div className="bg-blue-100 p-6 rounded-md shadow-md h-96 flex items-center justify-center">
                                        <p className="text-blue-700 text-lg font-semibold">Your shipping label is being prepared. Please wait...</p>
                                    </div>
                                ) : (
                                    <>
                                        {(() => {
                                            const url = labelDetails.labelUrl;
                                            const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.svg'];
                                            const hasImageExtension = imageExtensions.some(ext => url.toLowerCase().endsWith(ext));
                                            const hasPdfExtension = url.toLowerCase().endsWith('.pdf');

                                            const effectiveLabelUrl = (!hasImageExtension && !hasPdfExtension) ? url + '.pdf' : url;

                                            return effectiveLabelUrl.endsWith('.pdf') ? (
                                                <iframe
                                                    src={effectiveLabelUrl}
                                                    title="Shipping Label"
                                                    width="100%"
                                                    height="600px"
                                                    className="border rounded-md shadow-sm"
                                                    onLoad={handleLabelLoad}
                                                    onError={() => handleLabelError(false)}
                                                    key={effectiveLabelUrl} // Simplified key
                                                >
                                                    <p>Your browser does not support iframes. You can download the label <a href={effectiveLabelUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">here</a>.</p>
                                                </iframe>
                                            ) : (
                                                <img
                                                    src={effectiveLabelUrl}
                                                    alt="Shipping Label"
                                                    className="max-w-full h-auto rounded-md shadow-sm mx-auto"
                                                    onLoad={handleLabelLoad}
                                                    onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/300x200/FF0000/FFFFFF?text=Error+Loading+Label"; handleLabelError(true); }}
                                                    key={effectiveLabelUrl} // Simplified key
                                                />
                                            );
                                        })()}

                                        {/* Loading overlay/message */}
                                        {labelContentLoading && (
                                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-md">
                                                <p className="text-blue-700 text-center text-lg font-semibold">Loading shipping label content...</p>
                                            </div>
                                        )}

                                        {/* Error overlay/message */}
                                        {labelContentError && (
                                            <div className="absolute inset-0 bg-red-100 bg-opacity-90 flex flex-col items-center justify-center rounded-md border border-red-400 text-red-700 p-4">
                                                <p className="font-bold mb-2">Failed to load shipping label content.</p>
                                                <p className="text-sm mb-4">The file might not be ready or accessible.</p>
                                                <button
                                                    onClick={() => {
                                                        setLabelContentLoading(true);
                                                        setLabelContentError(false);
                                                        setLabelDetails({ ...labelDetails }); // Force re-render of iframe/img
                                                    }}
                                                    className="mt-2 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg"
                                                >
                                                    Retry Loading Label
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <p className="text-red-600 mt-4">No label URL provided. Please check the backend response.</p>
                        )}
                        <div className="mt-4 text-center">
                            {labelDetails?.labelUrl && ( // Only show buttons if labelUrl exists
                                <>
                                    <a
                                        href={labelDetails.labelUrl} // Use original URL for download/view
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200 ease-in-out mr-2"
                                        download // Suggests download to the browser
                                    >
                                        Download Shipping Label
                                    </a>
                                    <a
                                        href={labelDetails.labelUrl} // Use original URL for download/view
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200 ease-in-out"
                                    >
                                        View Shipping Label (New Tab)
                                    </a>
                                </>
                            )}
                        </div>
                        <button
                            onClick={handleNewOrder}
                            className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline transition duration-200 ease-in-out"
                        >
                            Start New Order
                        </button>
                    </div>

                    {nearestDropoff && (
                        <div className="mt-8 p-6 bg-yellow-50 rounded-xl shadow-inner border border-yellow-200 text-center">
                            <h3 className="text-xl font-semibold text-yellow-800 mb-3">Nearest Drop-off Location:</h3>
                            <p className="text-gray-800 font-medium">{nearestDropoff.name}</p>
                            <p className="text-600">{nearestDropoff.address}</p>
                            {nearestDropoff.lat && nearestDropoff.lng && (
                                <div className="mt-3">
                                    <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${nearestDropoff.lat},${nearestDropoff.lng}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline text-sm"
                                    >
                                        View on Google Maps
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Tailwind utility classes for form inputs and selects */}
            <style jsx>{`
                .form-input {
                    @apply shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500;
                }
                .form-select {
                    @apply shadow appearance-none border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white;
                }
            `}</style>
        </div>
    );
};

export default OrderForm;
